/**
 * Portal Dashboard Page
 * Dashboard adaptado aos serviços contratados pela entidade
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCalendar, FiShoppingBag, FiHeart, FiPackage, FiShoppingCart,
  FiBarChart2, FiCreditCard, FiClock, FiCheckCircle, FiList,
  FiTrendingUp, FiGift, FiAlertCircle, FiRefreshCw, FiArrowRight,
} from 'react-icons/fi';
import { MdPayments } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import portalService from '../../services/portal.service';
import type { PortalDashboardStats } from '../../types/portal.types';
import { ROLE_DESCRIPTIONS } from '../../types/portal.types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n?: number) =>
  Math.round(n ?? 0).toLocaleString('pt-CV', { maximumFractionDigits: 0 });

// ─── Card primitives ─────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
  textColor: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color, textColor, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-pagali-primary/30 transition-all' : ''}`}
  >
    <div className={`p-3 rounded-full ${color} flex-shrink-0`}>
      <span className={`text-xl ${textColor}`}>{icon}</span>
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{label}</p>
      <p className="text-xl font-bold text-gray-800 mt-0.5">{value}</p>
    </div>
    {onClick && <FiArrowRight className="ml-auto text-gray-300 flex-shrink-0" />}
  </div>
);

interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
  textColor: string;
  onClick: () => void;
}

const QuickAction: React.FC<QuickActionProps> = ({ icon, label, description, color, textColor, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-pagali-primary hover:shadow-md transition-all text-left w-full"
  >
    <div className={`p-2.5 rounded-lg ${color} flex-shrink-0`}>
      <span className={`text-lg ${textColor}`}>{icon}</span>
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-800">{label}</p>
      <p className="text-xs text-gray-500 truncate">{description}</p>
    </div>
    <FiArrowRight className="ml-auto text-gray-300 flex-shrink-0" />
  </button>
);

// ─── Main component ───────────────────────────────────────────────────────────

const PortalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<PortalDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = () => {
    setLoading(true);
    setError(null);
    portalService.getDashboard()
      .then((data) => {
        // API may return [] on SQL error — normalise to null
        setStats(Array.isArray(data) ? null : data);
      })
      .catch((err) => setError(err.message || 'Erro ao carregar dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  // Use serviceTypes from API response directly — no AuthContext dependency
  const serviceTypes: string[] = (((stats as any)?.serviceTypes || []) as string[])
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
  const hasECM = serviceTypes.some((code) => ['ECM', 'SHOP', 'E-COMMERCE', 'ECOMMERCE'].includes(code)) || user?.role === 'PFLOJ';
  const hasEVT = serviceTypes.some((code) => ['EVT', 'EVENT', 'EVENTS'].includes(code)) || user?.role === 'PFEVT';
  const hasDNT = serviceTypes.some((code) => ['DNT', 'DONATION', 'DONATIONS'].includes(code)) || user?.role === 'PFONG';
  const hasVCH = serviceTypes.some((code) => ['VCH', 'VOUCHER', 'VOUCHERS'].includes(code));

  const roleName = user?.role ? ROLE_DESCRIPTIONS[user.role as keyof typeof ROLE_DESCRIPTIONS] : '';
  const entityName = stats?.entidade?.DESIG || roleName || 'Portal';
  const entityCode = stats?.entidade?.CODIGO;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64 p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pagali-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{entityName}</h1>
          <div className="flex items-center gap-2 mt-1">
            {entityCode && (
              <span className="font-mono text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                {entityCode}
              </span>
            )}
            <span className="text-sm text-gray-500">Bem-vindo ao seu painel de gestão</span>
          </div>
        </div>
        <button
          onClick={loadData}
          className="p-2 rounded-lg text-gray-400 hover:text-pagali-primary hover:bg-pagali-primary/5 transition-colors flex-shrink-0"
          title="Atualizar"
        >
          <FiRefreshCw className="text-lg" />
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          <FiAlertCircle className="flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadData} className="ml-auto text-red-500 underline text-xs">Tentar novamente</button>
        </div>
      )}

      {/* ── Resumo Financeiro ── */}
      <section>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Resumo Financeiro (30 dias)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={<FiCheckCircle />}
            label="Total Recebido"
            value={`${fmt(stats?.pagamentos?.totalRecebido)} ECV`}
            color="bg-green-100" textColor="text-green-600"
            onClick={() => navigate('/portal/payments')}
          />
          <StatCard
            icon={<FiList />}
            label="Transacções"
            value={stats?.pagamentos?.totalTransacoes ?? 0}
            color="bg-blue-100" textColor="text-blue-600"
            onClick={() => navigate('/portal/payments')}
          />
          <StatCard
            icon={<FiClock />}
            label="Pendente"
            value={`${fmt(stats?.pagamentos?.totalPendente)} ECV`}
            color="bg-amber-100" textColor="text-amber-600"
          />
        </div>
      </section>

      {/* ── Loja Online (ECM) ── */}
      {hasECM && (
        <section>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Loja Online</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <StatCard
              icon={<FiPackage />}
              label="Produtos"
              value={stats?.loja?.totalProdutos ?? 0}
              color="bg-purple-100" textColor="text-purple-600"
              onClick={() => navigate('/portal/products')}
            />
            <StatCard
              icon={<FiShoppingCart />}
              label="Pedidos (30 dias)"
              value={stats?.loja?.pedidosMes ?? 0}
              color="bg-pink-100" textColor="text-pink-600"
              onClick={() => navigate('/portal/orders')}
            />
            <StatCard
              icon={<FiTrendingUp />}
              label="Vendas (30 dias)"
              value={`${fmt((stats?.loja as any)?.vendasMes)} ECV`}
              color="bg-green-100" textColor="text-green-600"
              onClick={() => navigate('/portal/orders')}
            />
            <StatCard
              icon={<FiClock />}
              label="Pedidos Pendentes"
              value={(stats?.loja as any)?.pedidosPendentes ?? 0}
              color="bg-amber-100" textColor="text-amber-600"
              onClick={() => navigate('/portal/orders?estado=PENDENTE')}
            />
          </div>
          {/* Link partilhável da loja */}
          {(stats as any)?.loja?.lojaSlug && (
            <div className="mt-3 flex items-center gap-3 bg-pagali-purple/5 border border-pagali-purple/20 rounded-xl px-4 py-3">
              <FiShoppingBag className="text-pagali-purple flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">Link da sua loja</p>
                <p className="text-sm font-medium text-pagali-purple truncate">
                  pagali.cv/loja/{(stats as any).loja.lojaSlug}
                </p>
              </div>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/loja/${(stats as any).loja.lojaSlug}`;
                  navigator.clipboard?.writeText(url).then(() => alert('Link copiado!')).catch(() => {});
                }}
                className="text-xs text-pagali-purple border border-pagali-purple/30 rounded-lg px-3 py-1.5 hover:bg-pagali-purple/10 transition-colors whitespace-nowrap"
              >
                Copiar
              </button>
            </div>
          )}
        </section>
      )}

      {/* ── Eventos (EVT) ── */}
      {hasEVT && (
        <section>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Eventos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              icon={<FiCalendar />}
              label="Total de Eventos"
              value={stats?.eventos?.totalEventos ?? 0}
              color="bg-indigo-100" textColor="text-indigo-600"
              onClick={() => navigate('/portal/events')}
            />
            <StatCard
              icon={<FiTrendingUp />}
              label="Eventos Activos"
              value={stats?.eventos?.eventosAtivos ?? 0}
              color="bg-violet-100" textColor="text-violet-600"
              onClick={() => navigate('/portal/events')}
            />
          </div>
        </section>
      )}

      {/* ── Doações (DNT) ── */}
      {hasDNT && (
        <section>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Doações</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              icon={<FiHeart />}
              label="Campanhas"
              value={stats?.doacoes?.totalCampanhas ?? 0}
              color="bg-rose-100" textColor="text-rose-600"
              onClick={() => navigate('/portal/donations')}
            />
            <StatCard
              icon={<FiTrendingUp />}
              label="Total Arrecadado"
              value={`${fmt(stats?.doacoes?.totalArrecadado)} ECV`}
              color="bg-red-100" textColor="text-red-600"
            />
          </div>
        </section>
      )}

      {/* ── Atividade Recente ── */}
      {stats?.atividadeRecente && stats.atividadeRecente.length > 0 ? (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Actividade Recente</h2>
            <button
              onClick={() => navigate('/portal/payments')}
              className="text-xs text-pagali-primary hover:underline flex items-center gap-1"
            >
              Ver tudo <FiArrowRight className="text-xs" />
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-50">
            {stats.atividadeRecente.slice(0, 8).map((a: any, i: number) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    a.estado === 'N' ? 'bg-green-500' :
                    a.estado === 'S' ? 'bg-blue-500' :
                    a.estado === 'F' ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{a.referencia}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(a.data).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-800">{fmt(a.valor)} ECV</p>
                  <p className={`text-xs ${
                    a.estado === 'N' ? 'text-green-600' :
                    a.estado === 'S' ? 'text-blue-600' :
                    a.estado === 'F' ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {a.estado === 'N' ? 'Pago' : a.estado === 'S' ? 'Submetido' : a.estado === 'F' ? 'Falhou' : a.estado}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Actividade Recente</h2>
          <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center text-gray-400">
            <FiList className="text-3xl mx-auto mb-2 opacity-40" />
            <p className="text-sm">Ainda não há transacções registadas</p>
          </div>
        </section>
      )}

      {/* ── Acções Rápidas ── */}
      <section>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Acções Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <QuickAction
            icon={<MdPayments />}
            label="Ver Pagamentos"
            description="Histórico de recebimentos"
            color="bg-green-100" textColor="text-green-600"
            onClick={() => navigate('/portal/payments')}
          />
          <QuickAction
            icon={<FiBarChart2 />}
            label="Relatórios"
            description="Análise de desempenho"
            color="bg-blue-100" textColor="text-blue-600"
            onClick={() => navigate('/portal/reports')}
          />
          {hasECM && (
            <>
              <QuickAction
                icon={<FiPackage />}
                label="Gerir Produtos"
                description="Adicionar e editar produtos"
                color="bg-purple-100" textColor="text-purple-600"
                onClick={() => navigate('/portal/products')}
              />
              <QuickAction
                icon={<FiShoppingCart />}
                label="Ver Encomendas"
                description="Pedidos recebidos na loja"
                color="bg-pink-100" textColor="text-pink-600"
                onClick={() => navigate('/portal/orders')}
              />
            </>
          )}
          {hasEVT && (
            <QuickAction
              icon={<FiCalendar />}
              label="Gerir Eventos"
              description="Criar e gerir eventos"
              color="bg-indigo-100" textColor="text-indigo-600"
              onClick={() => navigate('/portal/events')}
            />
          )}
          {hasVCH && (
            <QuickAction
              icon={<FiGift />}
              label="Vouchers"
              description="Gerir vouchers e promoções"
              color="bg-amber-100" textColor="text-amber-600"
              onClick={() => navigate('/portal/vouchers')}
            />
          )}
          {hasDNT && (
            <QuickAction
              icon={<FiHeart />}
              label="Campanhas de Doação"
              description="Ver e gerir campanhas"
              color="bg-rose-100" textColor="text-rose-600"
              onClick={() => navigate('/portal/donations')}
            />
          )}
          <QuickAction
            icon={<FiShoppingBag />}
            label="Meus Serviços"
            description="Gerir serviços contratados"
            color="bg-gray-100" textColor="text-gray-600"
            onClick={() => navigate('/portal/services')}
          />
          <QuickAction
            icon={<FiCreditCard />}
            label="Pagamentos Recebidos"
            description="Facturas e recibos"
            color="bg-teal-100" textColor="text-teal-600"
            onClick={() => navigate('/portal/payments')}
          />
        </div>
      </section>

    </div>
  );
};

export default PortalDashboard;
